import React, { useState } from 'react';
import useIsMobileDevice from '../hooks/useIsMobileDevice';
import { useStateContext } from '../contexts/ContextProvider';
import CartItemCard from '../components/CartItemCard';
import { validateCheckoutForm } from '../lib/validation';
import { checkout } from '../lib/apiService';

const Checkout = () => {
    const { selectedItemsInCart, totalPrice } = useStateContext();
    const isMobileDevice = useIsMobileDevice();
    const [userInfo, setUserInfo] = useState({
        name: '',
        email: '',
        phone: ''
    });
    const [paymentMethod, setPaymentMethod] = useState('CIB'); // CIB | EDAHABIA

    const [checkoutSpinner, setCheckoutSpinner] = useState(false);

    const [errors, setErrors] = useState({});

    const [currentIndex, setCurrentIndex] = useState(0);

    const changeIndex = (index) => {
        if (index < 0) setCurrentIndex(selectedItemsInCart.length - 1);
        else if (index >= selectedItemsInCart.length) setCurrentIndex(0);
        else setCurrentIndex(index);
    };

    const handleUserInfoChange = (e) => {
        setErrors({});
        setUserInfo({
            ...userInfo,
            [e.target.name]: e.target.value
        });
    };

    const handleCheckout = async () => {
        // Validate user input
        const validationErrors = validateCheckoutForm({
            ...userInfo,
            paymentMethod
        });
        if (validationErrors && Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        // Proceed with checkout
        setCheckoutSpinner(true);
        await checkout({
            ...userInfo,
            paymentMethod,
            selectedItemsInCart
        });
        setCheckoutSpinner(false);
    };

    const renderSelectedItems = () => (
        <div className={`mt-2 ${isMobileDevice && 'mb-50'} bg-slate-50 shadow-md rounded-md  pb-6`}>
            <h2 className='text-xl font-bold text-left p-4'>Items</h2>
            <div className="flex flex-row items-center gap-2">
                {/* PrevArrow */}
                <button
                    className={`p-2 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none ${
                        currentIndex === 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    onClick={() => changeIndex(currentIndex - 1)}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M15.75 19.5L8.25 12l7.5-7.5"
                        />
                    </svg>
                </button>

                {/* Current Item */}
                <CartItemCard item={selectedItemsInCart[currentIndex]} />

                {/* NextArrow */}
                <button
                    className={`p-2 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none ${
                        currentIndex === selectedItemsInCart.length - 1
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                    }`}
                    onClick={() => changeIndex(currentIndex - 1)}
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="w-6 h-6"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M8.25 4.5l7.5 7.5-7.5 7.5"
                        />
                    </svg>
                </button>
            </div>
        </div>
    );

    return (
        <div className='w-full bg-gray-200 pb-10'>
            <div className={`relative w-11/12 flex flex-row ${isMobileDevice && 'overflow-y-auto h-screen flex-col'} mx-auto pt-10 gap-2`}>
                <section className={`${isMobileDevice ? 'w-full' : 'w-8/12'} shadow-md rounded-md bg-slate-50 p-4`}>
                    {/* User Info */}
                    <h1 className='text-2xl font-bold text-left text-gray-500'>User Info</h1>
                    <div className='pr-1 pt-4'>
                        {/* Name */}
                        <div className='flex flex-col items-start gap-1 p-2'>
                            <h3 className='text-md font-bold text-left'>Name <span className='text-red-500'>*</span></h3>
                            <input type='text' name='name' className='border rounded-md p-2 w-full' value={userInfo.name} onChange={handleUserInfoChange} />
                            {errors.name && <p className='text-sm text-red-500'>{errors.name}</p>}
                        </div>
                        {/* Email */}
                        <div className='flex flex-col items-start gap-1 p-2'>
                            <h3 className='text-md font-bold text-left'>Email <span className='text-red-500'>*</span></h3>
                            <input type='email' name='email' className='border rounded-md p-2 w-full' value={userInfo.email} onChange={handleUserInfoChange} />
                            {errors.email && <p className='text-sm text-red-500'>{errors.email}</p>}
                        </div>
                        {/* Phone */}
                        <div className='flex flex-col items-start gap-1 p-2'>
                            <h3 className='text-md font-bold text-left'>Phone <span className='text-red-500'>*</span></h3>
                            <input type='tel' name='phone' className='border rounded-md p-2 w-full' value={userInfo.phone} onChange={handleUserInfoChange} />
                            {errors.phone && <p className='text-sm text-red-500'>{errors.phone}</p>}
                        </div>
                    </div>
                    {/* Payment Method */}
                    <h1 className='text-2xl font-bold text-left text-gray-500'>Payment Method</h1>
                    <div className='pr-1 pt-4'>
                        <div className={`${!isMobileDevice && 'w-1/4'} mx-2`}>
                            <div
                                className={`flex flex-row items-center justify-between py-1 px-2 border border-gray-300 rounded-md hover:cursor-pointer hover:bg-gray-100 ${paymentMethod === 'CIB' ? 'border-green-500 shadow-lg' : ''}`}
                                onClick={() => setPaymentMethod('CIB')}
                            >
                                <div className='text-md'>CIB</div>
                                <img  src={require('../assets/images/cib.png')} alt="CIB" className="w-10 h-12 object-cover rounded border border-gray-300" />
                            </div>
                            <div
                                className={`flex flex-row items-center justify-between py-1 mt-2 px-2 border border-gray-300 rounded-md hover:cursor-pointer hover:bg-gray-100 ${paymentMethod === 'EDAHABIA' ? 'border-green-500  shadow-lg' : ''}`}
                                onClick={() => setPaymentMethod('EDAHABIA')}
                            >
                                <div className='text-md'>EDAHABIA</div>
                                <img src={require('../assets/images/edahabia.png')} alt="EDAHABIA" className="w-10 h-12 object-cover rounded border border-gray-300" />
                            </div>
                        </div>
                    </div>
                    {errors.paymentMethod && <p className='text-sm text-red-500'>{errors.paymentMethod}</p>}
                </section>

                {isMobileDevice && (
                    <>
                        {renderSelectedItems()}
                        <section className="sticky bottom-0 w-full flex flex-row justify-between items-center bg-slate-50 shadow-md rounded-md p-4 z-10">
                            <p className="flex-1/2">DA {totalPrice}</p>
                            <button
                                className="flex flex-row items-center justify-center flex-1/2 bg-blue-500 hover:bg-blue-400 text-white rounded-2xl px-4 py-2"
                                onClick={() => handleCheckout()}
                            >
                                <span>Checkout</span>
                                {/* Spinner */}
                                {checkoutSpinner && (
                                <div className="inline-block ml-2 animate-spin">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                    </svg>
                                </div>
                                )}
                            </button>
                        </section>
                    </>
                    )}

                {/* Summary */}
                {!isMobileDevice && (
                    <section className='sticky top-20 w-4/12 max-h-[200px]'>
                        <div className='bg-slate-50 shadow-md rounded-md p-4'>
                        <h1 className="text-2xl font-bold text-left pb-4">
                            Summary
                        </h1>
                        <div className='flex flex-row justify-between gap-2'>
                            <h3 className='text-lg font-bold'>Total</h3>
                            <p>DA {totalPrice}</p>
                        </div>
                        <button
                            className='flex flex-row items-center justify-center bg-blue-500 hover:bg-blue-400 text-white rounded-xl p-2 w-full mt-6'
                            onClick={() => handleCheckout()}
                        >
                            <span>Checkout</span>
                            {/* Spinner */}
                            {checkoutSpinner && (
                                <div className='inline-block ml-2 animate-spin'>
                                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                </div>
                            )}
                        </button>
                        </div>
                        {renderSelectedItems()}
                    </section>
                )}  
            </div>
        
        </div>
    );
}

export default Checkout;
